import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, FileText, Building2, BarChart3, GraduationCap, Rocket, ShieldAlert, ShieldCheck, Info } from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import logo from '../logo_CVUT.jpg';
import UserMenu from './UserMenu';
import LanguageSwitcher from './LanguageSwitcher';
import { useAuth } from '../auth/AuthContext';
import { hasAnyRole, COMPANY_ROLES, ALUMNI_ROLES, ADMIN_ROLES } from '../auth/roles';

const IS_AUTH_ENABLED: boolean = process.env.REACT_APP_AUTH_ENABLED === 'true';

type Item = {
  key: string;
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  matches: (pathname: string) => boolean;
  // Roles allowed to see this item; omit for items open to any signed-in user.
  requiredRoles?: readonly string[];
};

const DISCOVER_ITEMS: Item[] = [
  {
    key: 'people',
    to: '/people',
    icon: Users,
    matches: (p) => p.startsWith('/people') || p.startsWith('/person'),
  },
  {
    key: 'results',
    to: '/outcomes',
    icon: FileText,
    matches: (p) => p.startsWith('/outcomes') || p.startsWith('/outcome'),
  },
];

const UNDERSTAND_ITEMS: Item[] = [
  {
    key: 'insights',
    to: '/insights',
    icon: BarChart3,
    matches: (p) =>
      p.startsWith('/insights') ||
      p.startsWith('/dashboard') ||
      p.startsWith('/network'),
  },
  {
    key: 'companies',
    to: '/companies',
    icon: Building2,
    matches: (p) => p.startsWith('/companies') || p.startsWith('/company'),
    requiredRoles: COMPANY_ROLES,
  },
  {
    key: 'ctuSpinoffs',
    to: '/ctu-companies',
    icon: Rocket,
    matches: (p) => p.startsWith('/ctu-companies'),
  },
  {
    key: 'alumni',
    to: '/alumni',
    icon: GraduationCap,
    matches: (p) => p.startsWith('/alumni'),
    requiredRoles: ALUMNI_ROLES,
  },
];

const ADMIN_ITEMS: Item[] = [
  {
    key: 'admin',
    to: '/admin',
    icon: ShieldAlert,
    matches: (p) => p.startsWith('/admin'),
    requiredRoles: ADMIN_ROLES,
  },
];

export default function AppSidebar(): React.ReactElement {
  const { t } = useTranslation('menu');
  const { pathname } = useLocation();
  const { user } = useAuth();

  // Hide nav entries the user's roles don't grant (UX mirror of the backend policy).
  const visible = (items: Item[]) =>
    items.filter((item) => hasAnyRole(user?.roles, item.requiredRoles ?? []));

  const renderItem = (item: Item) => {
    const Icon = item.icon;
    const isActive = item.matches(pathname);
    return (
      <SidebarMenuItem key={item.key}>
        <SidebarMenuButton asChild isActive={isActive} tooltip={t(item.key)}>
          <Link to={item.to}>
            <Icon className="h-4 w-4" />
            <span>{t(item.key)}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <Link
          to="/"
          className="flex items-center gap-2.5 px-2 py-1.5 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:justify-center"
        >
          <img
            src={logo}
            alt="CTU"
            className="h-10 w-10 object-contain rounded-md shrink-0 group-data-[collapsible=icon]:h-7 group-data-[collapsible=icon]:w-7"
          />
          <span className="font-semibold text-base tracking-tight group-data-[collapsible=icon]:hidden">
            Uniconnect
          </span>
        </Link>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{t('groupDiscover')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>{visible(DISCOVER_ITEMS).map(renderItem)}</SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>{t('groupUnderstand')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>{visible(UNDERSTAND_ITEMS).map(renderItem)}</SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {visible(ADMIN_ITEMS).length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>{t('groupAdmin')}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>{visible(ADMIN_ITEMS).map(renderItem)}</SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter>
        <Link
          to="/about"
          className="flex items-center gap-2 px-2 py-1.5 text-xs text-ctu-text-muted transition-colors hover:text-ctu-text group-data-[collapsible=icon]:justify-center"
          title={t('about')}
        >
          <Info className="h-4 w-4 shrink-0" />
          <span className="group-data-[collapsible=icon]:hidden">{t('about')}</span>
        </Link>
        <Link
          to="/privacy"
          className="flex items-center gap-2 px-2 py-1.5 text-xs text-ctu-text-muted transition-colors hover:text-ctu-text group-data-[collapsible=icon]:justify-center"
          title={t('privacy')}
        >
          <ShieldCheck className="h-4 w-4 shrink-0" />
          <span className="group-data-[collapsible=icon]:hidden">{t('privacy')}</span>
        </Link>
        <div className="flex items-center justify-between gap-2 px-2 py-1.5 group-data-[collapsible=icon]:hidden">
          <LanguageSwitcher />
          {IS_AUTH_ENABLED && <UserMenu />}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
